"use client";
import React, { useEffect, useState } from "react";
import RCSlider, { type SliderProps } from "rc-slider-v10";
import inRange from "lodash/inRange";
import isEqual from "lodash/isEqual";

import TextInput from "@stadium/ui/src/components/form/TextInput";
import Label from "@stadium/ui/src/components/form/Label";

import defaultStyle from "./main.module.scss";

interface HistogramData {
  count: number;
  from: number;
  to: number;
}

interface RangeSliderProps extends SliderProps<number[]> {
  showMinRangeInput?: boolean;
  showMaxRangeInput?: boolean;
  rangeFormContClass?: string;
  minPriceColClass?: string;
  maxPriceColClass?: string;
  placeholderPrefix?: string;
  histogramData?: HistogramData[];
  maxTextInputLabel?: string;
  minTextInputLabel?: string;
  prefix?: string;
  onInputChange?: (value: string, type: "minValue" | "maxValue") => void;
  /** Min/max inputs (blur/submit) only; use with onAfterChange for full “commit” coverage. */
  onInputCommit?: (next: number[], previous: number[]) => void;
}

const emptyArr = [];

const RangeSlider: React.FC<RangeSliderProps> = ({
  showMaxRangeInput = true,
  showMinRangeInput = true,
  styles = defaultStyle,
  onChange,
  onInputCommit,
  onInputChange,
  value,
  rangeFormContClass = "",
  minPriceColClass = "",
  maxPriceColClass = "",
  placeholderPrefix = "",
  maxTextInputLabel = "MAX.",
  minTextInputLabel = "MIN.",
  histogramData = emptyArr,
  prefix = "",
  ...rest
}) => {
  const { min: absoluteMinValue, max: absoluteMaxValue } = rest;
  const [minVal, maxVal] = value;
  const [minValue, setMinValue] = useState(minVal);
  const [maxValue, setMaxValue] = useState(maxVal);

  useEffect(() => setMinValue(minVal), [minVal]);
  useEffect(() => setMaxValue(maxVal), [maxVal]);

  const setRangeValue = (changedValue: number[]) => {
    const [min, max] = changedValue;
    setMinValue(min);
    setMaxValue(max);
    if (!isEqual(changedValue, value)) {
      onChange(changedValue);
    }
  };

  const validateValue = () => {
    const previous = [...value];
    const rangeValue = [minValue, maxValue];
    if (!inRange(minValue, absoluteMinValue, maxVal + 1)) {
      const updatedMinValue = value[0] || absoluteMinValue;
      rangeValue[0] = updatedMinValue;
    }
    if (!inRange(maxValue, minValue + 1, absoluteMaxValue + 1)) {
      const updatedMaxValue = value[1] || absoluteMaxValue;
      rangeValue[1] = updatedMaxValue;
    }
    setRangeValue(rangeValue);
    if (!isEqual(rangeValue, previous)) {
      onInputCommit?.(rangeValue, previous);
    }
  };

  const showMinMaxValueInput = showMaxRangeInput || showMinRangeInput;

  const renderHistogram = () => {
    if (
      !histogramData?.length ||
      (!absoluteMinValue && absoluteMinValue !== 0) ||
      !absoluteMaxValue
    ) {
      return null;
    }

    const maxCount = Math.max(...histogramData.map((b) => b.count));

    const totalRange = absoluteMaxValue - absoluteMinValue;

    return (
      <div className={styles.histogramContainer}>
        {histogramData.map((value, index) => {
          const leftPercent = ((value.from - absoluteMinValue) / totalRange) * 100;

          const widthPercent = ((value.to - value.from) / totalRange) * 100;

          const heightPercent = (value.count / maxCount) * 100;

          return (
            <div
              key={index}
              className={styles.histogramBar}
              style={{
                left: `${leftPercent}%`,
                width: `${widthPercent}%`,
                height: `${heightPercent}%`,
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.rangeContainer}>
      {histogramData?.length > 0 && renderHistogram()}
      <RCSlider
        range
        onChange={setRangeValue}
        value={value}
        {...rest}
      />
      {showMinMaxValueInput && (
        <form
          id="min-max-price-ranges"
          onSubmit={(e) => {
            e.preventDefault();
            validateValue();
          }}
        >
          <div className={`${styles.rangeFormCont} ${rangeFormContClass}`}>
            {showMinRangeInput && (
              <div className={`${styles.rangeMinCol} ${minPriceColClass}`}>
                {prefix && <span>{prefix}</span>}
                <TextInput
                  id="min-price-range"
                  name="min-price-range"
                  inputMode="numeric"
                  placeholder={`${placeholderPrefix} ${String(absoluteMinValue)}`.trim()}
                  value={String(minValue)}
                  type="number"
                  onChange={(target) => {
                    setMinValue(Number(target.value));
                    onInputChange?.(target.value, "minValue");
                  }}
                  disabled={rest.disabled}
                  className={styles.priceInput}
                  onBlur={validateValue}
                  max={Math.min(maxValue - 1, absoluteMaxValue)}
                  min={absoluteMinValue}
                  label={<Label showIcon={false}>{minTextInputLabel}</Label>}
                />
              </div>
            )}
            {showMaxRangeInput && (
              <div className={`${styles.rangeMaxCol} ${maxPriceColClass}`}>
                {prefix && <span>{prefix}</span>}
                <TextInput
                  id="max-price-range"
                  name="max-price-range"
                  inputMode="numeric"
                  placeholder={`${placeholderPrefix} ${String(absoluteMaxValue)}`.trim()}
                  value={String(maxValue)}
                  type="number"
                  disabled={rest.disabled}
                  className={styles.priceInput}
                  onChange={(target) => {
                    setMaxValue(Number(target.value));
                    onInputChange?.(target.value, "maxValue");
                  }}
                  onBlur={validateValue}
                  label={<Label showIcon={false}>{maxTextInputLabel}</Label>}
                  max={absoluteMaxValue}
                  min={Math.max(minValue + 1, absoluteMinValue)}
                />
              </div>
            )}
          </div>
          {/* Dummy submit element added to make sure "Enter" on a form field submits the form */}
          <input type="submit" style={{ display: "none" }} />
        </form>
      )}
    </div>
  );
};

export default RangeSlider;
